import { PickerSearch, matchesQuery } from "@/components/admin/picker-search";
import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { ChatPanel, useChatUnreadCount } from "@/components/messages/chat-panel";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authClient } from "@/lib/auth-client";
import { apiGet, apiPost } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Loader2, Send, Users, Building2, User, CheckCircle2,
  StickyNote, MessageSquare, Clock, CheckCheck, HelpCircle,
  UserCheck, Pencil, Megaphone, FileCheck2,
} from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/notes")({
  component: NotesPage,
  validateSearch: (search: Record<string, unknown>) => ({
    userId: typeof search.userId === "string" ? search.userId : undefined,
  }),
});

type Division = { id: string; name: string };
type UserItem = { id: string; name: string; email: string; divisionName?: string | null };
type NoteResponse = "acknowledged" | "completed" | "need_clarification";
type NoteSource = "approval" | "manual" | "broadcast" | "assignment";
type SentNote = {
  id: string;
  content: string;
  source: NoteSource;
  isReadByWorker: boolean;
  response: NoteResponse | null;
  respondedAt: string | null;
  createdAt: string;
  workerId: string;
  workerName: string;
  workerEmail: string;
};

const RESPONSE_META: Record<NoteResponse, { label: string; icon: typeof CheckCheck; cls: string }> = {
  acknowledged: { label: "Acknowledged", icon: CheckCheck, cls: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" },
  completed: { label: "Completed", icon: CheckCheck, cls: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300" },
  need_clarification: { label: "Needs Clarification", icon: HelpCircle, cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" },
};

const SOURCE_META: Record<NoteSource, { label: string; icon: typeof StickyNote; cls: string }> = {
  approval: { label: "Approve page", icon: UserCheck, cls: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300" },
  manual: { label: "Note", icon: Pencil, cls: "bg-slate-100 text-slate-700 dark:bg-slate-800/50 dark:text-slate-300" },
  broadcast: { label: "Broadcast", icon: Megaphone, cls: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300" },
  assignment: { label: "Assignment", icon: FileCheck2, cls: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300" },
};

function NotesPage() {
  const { data: session } = authClient.useSession();
  const queryClient = useQueryClient();
  const isSuperAdmin = session?.user?.role === "admin";
  const { userId: replyToUserId } = Route.useSearch();

  const [tab, setTab] = useState<"compose" | "sent" | "chat">("compose");
  const [recipientType, setRecipientType] = useState<"all" | "division" | "users">(
    replyToUserId ? "users" : "all"
  );
  const [selectedDivisionIds, setSelectedDivisionIds] = useState<string[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>(
    replyToUserId ? [replyToUserId] : []
  );
  const [content, setContent] = useState("");

  // Deep-linked from a "Reply to <worker>" action (e.g. Needs Clarification
  // banner) -- pre-select just that worker so a reply can't accidentally
  // broadcast to everyone.
  useEffect(() => {
    if (!replyToUserId) return;
    setTab("compose");
    setRecipientType("users");
    setSelectedUserIds([replyToUserId]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replyToUserId]);

  const { data: divisionsData } = useQuery({
    queryKey: ["admin-divisions"],
    queryFn: () => apiGet<{ data: Division[] }>("/api/admin/divisions"),
  });
  const divisions = divisionsData?.data ?? [];

  const { data: usersData } = useQuery({
    queryKey: ["users-for-notes"],
    queryFn: () => apiGet<{ data: UserItem[] }>("/api/users"),
    enabled: recipientType === "users",
  });
  const [userQuery, setUserQuery] = useState("");
  const allUsers = (usersData?.data ?? []).filter((u) => u.id !== session?.user?.id && matchesQuery(userQuery, u.name, u.email));

  const { data: sentData, isLoading: sentLoading } = useQuery({
    queryKey: ["admin-notes-sent"],
    queryFn: () => apiGet<{ data: SentNote[] }>("/api/admin/users/notes/sent"),
    enabled: tab === "sent",
    staleTime: 60_000,
  });

  // Sort: unresponded first (within that: unread > read), then responded (newest respondedAt first)
  const sentNotes = (() => {
    const raw = sentData?.data ?? [];
    const unresponded = raw.filter((n) => !n.response).sort((a, b) => {
      if (a.isReadByWorker !== b.isReadByWorker) return a.isReadByWorker ? 1 : -1;
      return b.createdAt.localeCompare(a.createdAt);
    });
    const responded = raw.filter((n) => n.response).sort((a, b) =>
      (b.respondedAt ?? "").localeCompare(a.respondedAt ?? "")
    );
    return [...unresponded, ...responded];
  })();

  const sendNote = useMutation({
    mutationFn: () =>
      apiPost<{ count: number }>("/api/admin/users/notes/broadcast", {
        content: content.trim(),
        recipientType,
        divisionIds: recipientType === "division" ? selectedDivisionIds : undefined,
        userIds: recipientType === "users" ? selectedUserIds : undefined,
      }),
    onSuccess: (res) => {
      toast.success(`Note sent to ${res.count} worker${res.count !== 1 ? "s" : ""}`);
      setContent("");
      setSelectedDivisionIds([]);
      setSelectedUserIds([]);
      queryClient.invalidateQueries({ queryKey: ["admin-notes-sent"] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Failed to send note"),
  });

  function handleSend() {
    if (!content.trim()) { toast.error("Note content is required"); return; }
    if (recipientType === "division" && selectedDivisionIds.length === 0) { toast.error("Select at least one division"); return; }
    if (recipientType === "users" && selectedUserIds.length === 0) { toast.error("Select at least one user"); return; }
    sendNote.mutate();
  }

  const recipientOptions = [
    { id: "all" as const, label: isSuperAdmin ? "All users" : "All my crew", description: isSuperAdmin ? "Every active worker in the organization" : "Every active worker in your division", icon: Users },
    { id: "division" as const, label: "By division", description: "Every active worker in one or more divisions", icon: Building2 },
    { id: "users" as const, label: "Specific workers", description: "Choose individual workers by name", icon: User },
  ];

  const unrespondedCount = sentNotes.filter((n) => !n.response).length;
  const { data: chatUnread } = useChatUnreadCount();
  const chatUnreadCount = chatUnread?.count ?? 0;

  return (
    <div className="max-w-2xl space-y-4">
      {/* Page header */}
      <div>
        <h2 className="text-lg font-semibold text-text-primary">Messages</h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          Send notes to workers and track their responses, and chat with supervisors and other admins.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 rounded-lg border border-border p-1 bg-muted/30 w-fit">
        {[
          { id: "compose" as const, label: "Compose", icon: Send },
          { id: "sent" as const, label: `Sent${unrespondedCount > 0 ? ` · ${unrespondedCount} pending` : ""}`, icon: MessageSquare },
          { id: "chat" as const, label: `Chat${chatUnreadCount > 0 ? ` · ${chatUnreadCount} new` : ""}`, icon: MessageSquare },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={cn(
              "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
              tab === id ? "bg-surface-raised text-text-primary shadow-sm" : "text-muted-foreground hover:text-text-primary"
            )}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </button>
        ))}
      </div>

      {/* ── Chat tab ─────────────────────────────────────────────────────── */}
      {tab === "chat" && <ChatPanel />}

      {/* ── Compose tab ─────────────────────────────────────────────────── */}
      {tab === "compose" && (
        <div className="space-y-5">
          {/* Recipient type picker */}
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Send to</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {recipientOptions.map(({ id, label, description, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => { setRecipientType(id); setSelectedDivisionIds([]); setSelectedUserIds([]); }}
                  className={cn(
                    "flex flex-col items-start gap-1 rounded-lg border px-4 py-3 text-left transition-colors",
                    recipientType === id
                      ? "border-paris-red bg-paris-red/10 text-paris-red-text"
                      : "border-border bg-card hover:bg-muted/40"
                  )}
                >
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    <span className="text-sm font-medium">{label}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{description}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Division picker */}
          {recipientType === "division" && divisions.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Select divisions</p>
              <div className="flex flex-wrap gap-2">
                {divisions.map((d) => (
                  <button key={d.id} type="button"
                    onClick={() => setSelectedDivisionIds((p) => p.includes(d.id) ? p.filter((x) => x !== d.id) : [...p, d.id])}
                    className={cn(
                      "px-3 py-1.5 rounded-full border text-sm transition-colors",
                      selectedDivisionIds.includes(d.id)
                        ? "border-paris-red bg-paris-red/10 text-paris-red-text font-medium"
                        : "border-border bg-card hover:bg-muted/40 text-muted-foreground"
                    )}
                  >{d.name}</button>
                ))}
              </div>
            </div>
          )}

          {/* User picker */}
          {recipientType === "users" && (
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Select workers
                {selectedUserIds.length > 0 && (
                  <span className="ml-2 normal-case font-normal text-paris-red-text">{selectedUserIds.length} selected</span>
                )}
              </p>
              {!usersData ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading users…
                </div>
              ) : (
                <div className="space-y-2">
                <PickerSearch value={userQuery} onChange={setUserQuery} placeholder="Search workers…" />
                <div className="rounded-lg border border-border divide-y max-h-60 overflow-y-auto">
                  {allUsers.length === 0 && <p className="p-3 text-center text-xs text-muted-foreground">No workers found</p>}
                  {allUsers.map((u) => (
                    <button key={u.id} type="button"
                      onClick={() => setSelectedUserIds((p) => p.includes(u.id) ? p.filter((x) => x !== u.id) : [...p, u.id])}
                      className={cn(
                        "w-full flex items-center justify-between px-4 py-2.5 text-left transition-colors",
                        selectedUserIds.includes(u.id) ? "bg-paris-red/8" : "hover:bg-muted/40"
                      )}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{u.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                      </div>
                      {selectedUserIds.includes(u.id) && <CheckCircle2 className="h-4 w-4 text-paris-red shrink-0 ml-3" />}
                    </button>
                  ))}
                </div>
                </div>
              )}
            </div>
          )}

          {/* Note content */}
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Message</p>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Write your note here…"
              rows={5}
              disabled={sendNote.isPending}
              className="w-full rounded-lg border border-border bg-surface-base px-4 py-3 text-sm placeholder:text-muted-foreground/60 resize-none focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
            />
            <p className="text-xs text-muted-foreground text-right">{content.length} chars</p>
          </div>

          <Button onClick={handleSend} disabled={sendNote.isPending || !content.trim()} className="w-full sm:w-auto">
            {sendNote.isPending
              ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>
              : <><Send className="h-4 w-4 mr-2" /> Send Note</>
            }
          </Button>
        </div>
      )}

      {/* ── Sent tab ─────────────────────────────────────────────────────── */}
      {tab === "sent" && (
        <div className="space-y-2">
          {sentLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          )}

          {!sentLoading && sentNotes.length === 0 && (
            <div className="flex flex-col items-center gap-2 py-12 text-muted-foreground">
              <StickyNote className="h-8 w-8 opacity-30" />
              <p className="text-sm">No notes sent yet</p>
            </div>
          )}

          {sentNotes.map((note) => {
            const resp = note.response ? RESPONSE_META[note.response] : null;
            const isPending = !note.response;
            const src = SOURCE_META[note.source] ?? SOURCE_META.manual;
            return (
              <div
                key={note.id}
                className={cn(
                  "rounded-xl border px-4 py-3 space-y-2 text-sm transition-colors",
                  isPending && !note.isReadByWorker
                    ? "border-amber-300/50 bg-amber-50/40 dark:bg-amber-900/10"
                    : isPending
                    ? "border-border bg-surface-base"
                    : "border-border bg-surface-base/50"
                )}
              >
                {/* Header row */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center shrink-0 text-xs font-semibold text-muted-foreground">
                      {note.workerName?.charAt(0)?.toUpperCase() ?? "?"}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{note.workerName}</p>
                      <p className="text-[11px] text-muted-foreground">{note.workerEmail}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={cn("text-[11px] font-medium px-2 py-0.5 rounded-full flex items-center gap-1", src.cls)}>
                      <src.icon className="h-3 w-3" />
                      {src.label}
                    </span>
                    {resp ? (
                      <span className={cn("text-[11px] font-medium px-2 py-0.5 rounded-full flex items-center gap-1", resp.cls)}>
                        <resp.icon className="h-3 w-3" />
                        {resp.label}
                      </span>
                    ) : (
                      <span className="text-[11px] font-medium text-amber-600 dark:text-amber-400 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {note.isReadByWorker ? "Read, no response" : "Not yet read"}
                      </span>
                    )}
                  </div>
                </div>

                {/* Note content */}
                <p className="text-muted-foreground text-[13px] leading-relaxed line-clamp-3 pl-9">{note.content}</p>

                {/* Footer */}
                <div className="flex items-center justify-between pl-9 pt-0.5">
                  <span className="text-[11px] text-muted-foreground">
                    Sent {new Date(note.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                    {" "}
                    {new Date(note.createdAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                  </span>
                  {note.respondedAt && (
                    <span className="text-[11px] text-muted-foreground">
                      Responded {new Date(note.respondedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
